import { useRef, useState } from "react";
import { ImagePlus, Loader2, Send, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export function Composer({ onSend, disabled }) {
  const [text, setText] = useState("");
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [consented, setConsented] = useState(false);
  const [askConsent, setAskConsent] = useState(false);
  const input = useRef(null);

  const pick = () => (consented ? input.current?.click() : setAskConsent(true));

  const onFiles = async (e) => {
    const list = Array.from(e.target.files || []).slice(0, 4 - files.length);
    e.target.value = "";
    if (!list.length) return;
    setUploading(true);
    try {
      for (const f of list) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/uploads", fd);
        setFiles((prev) => [...prev, { id: data.id, url: URL.createObjectURL(f) }]);
      }
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setUploading(false);
    }
  };

  const submit = (e) => {
    e?.preventDefault();
    if (disabled || uploading || (!text.trim() && !files.length)) return;
    onSend(text.trim(), files.map((f) => f.id));
    setText("");
    setFiles([]);
  };

  return (
    <form onSubmit={submit} className="rounded-2xl border border-[#E6DBCF] bg-[#FDFBF7] p-3 shadow-[0_10px_35px_rgba(44,24,16,0.06)]" data-testid="chat-composer">
      {files.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-2">
          {files.map((f) => (
            <div key={f.id} className="relative" data-testid={`composer-attachment-${f.id}`}>
              <img src={f.url} alt="attachment preview" className="h-16 w-16 rounded-lg object-cover" />
              <button type="button" onClick={() => setFiles(files.filter((x) => x.id !== f.id))} className="absolute -right-1.5 -top-1.5 rounded-full bg-[#2C1810] p-0.5 text-white" data-testid={`composer-remove-attachment-${f.id}`}>
                <X size={12} />
              </button>
            </div>
          ))}
        </div>
      )}
      <div className="flex items-end gap-2">
        <button type="button" onClick={pick} disabled={uploading || files.length >= 4} data-testid="attach-photo-btn" title="Attach brow or label photo" className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#E6DBCF] text-[#6E5549] transition-colors hover:bg-[#FDF2F0] hover:text-[#2C1810] disabled:opacity-50">
          {uploading ? <Loader2 size={16} className="animate-spin" /> : <ImagePlus size={16} />}
        </button>
        <input ref={input} type="file" accept="image/jpeg,image/png,image/webp" multiple hidden onChange={onFiles} data-testid="attach-photo-input" />
        <textarea
          data-testid="chat-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && submit(e)}
          rows={1}
          placeholder="Describe the case, answer Ink Genius, or attach a photo…"
          className="max-h-40 min-h-[40px] flex-1 resize-none bg-transparent px-2 py-2.5 text-sm text-[#2C1810] placeholder:text-[#A88B7D] focus:outline-none"
        />
        <button type="submit" disabled={disabled || uploading || (!text.trim() && !files.length)} data-testid="chat-send-btn" className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#2C1810] text-[#FDFBF7] transition-colors hover:bg-[#3D2317] disabled:opacity-40">
          {disabled ? <Loader2 size={16} className="animate-spin" /> : <Send size={15} />}
        </button>
      </div>
      <AlertDialog open={askConsent} onOpenChange={setAskConsent}>
        <AlertDialogContent data-testid="photo-consent-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl">Before you attach photos</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2 text-sm leading-relaxed text-[#6E5549]">
              <span className="block">Please use client photos only with consent, and de-identify them (crop to the brow area, no names or faces where avoidable).</span>
              <span className="block">For the most useful observations: natural or consistent lighting, no filters, multiple angles. Label photos of your pigments are welcome.</span>
              <span className="block">Photos are supplemental — Ink Genius cannot determine undertone, depth, pigment chemistry or healing readiness from an image.</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="photo-consent-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="photo-consent-confirm"
              className="bg-[#2C1810] hover:bg-[#3D2317]"
              onClick={() => {
                setConsented(true);
                setTimeout(() => input.current?.click(), 50);
              }}
            >
              I confirm consent & de-identification
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </form>
  );
}
